import { useEffect, useState } from "react";
import { t } from "../i18n";
import { MapPin, Pencil, Trash2, Crown, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useModals } from "../context/ModalsContext";
import { TIPO_ICON } from "./InteractionSheet";
import { fmtFecha, fmtFechaHora, fmtMonto, tipoLabel } from "../lib/data";
import { RedFlagIcon, RED } from "./Avatar";

export function Timeline({ person }) {
  const { openInteraction, version, bump } = useModals();
  const [its, setIts] = useState([]);
  const [citas, setCitas] = useState([]);

  useEffect(() => {
    api.get("/interactions", { params: { person_id: person.id } }).then((r) => setIts(r.data)).catch(() => {});
    api.get("/appointments", { params: { person_id: person.id } }).then((r) => setCitas(r.data)).catch(() => {});
  }, [person.id, version]);

  const remove = async (id) => {
    try { await api.delete(`/interactions/${id}`); toast.success("Eliminado"); bump(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const now = Date.now();
  const futuras = citas.filter((c) => c.estado === "pendiente" && new Date(c.inicio).getTime() >= now).sort((a, b) => a.inicio.localeCompare(b.inicio));
  const entries = [
    ...its.map((i) => ({ kind: "it", fecha: i.fecha, data: i })),
    ...citas.filter((c) => !(c.estado === "pendiente" && new Date(c.inicio).getTime() >= now) && c.estado !== "realizada").map((c) => ({ kind: "cita", fecha: c.inicio, data: c })),
    ...(person.coronada?.activa ? [{ kind: "corona", fecha: person.coronada.fecha }] : []),
    ...(person.red_flag?.fecha ? [{ kind: "red_flag", fecha: person.red_flag.fecha }] : []),
    ...(person.red_flag?.fecha_quitada && !person.red_flag?.activa ? [{ kind: "red_flag_off", fecha: person.red_flag.fecha_quitada }] : []),
  ].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));

  return (
    <div className="surface p-4" data-testid="timeline">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("timeline.title")}</h3>
      {futuras.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="timeline-futuras">
          {futuras.map((c) => (
            <div key={c.id} data-testid={`timeline-cita-${c.id}`} className="flex items-start gap-3 rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 p-2.5">
              <CalendarClock size={16} className="text-[#d4af37] mt-0.5" />
              <div className="text-sm"><p className="font-medium">{t("timeline.nextAppointment", { t: tipoLabel(c.tipo) })}</p>
                <p className="text-xs text-muted-foreground">{fmtFechaHora(c.inicio)}{c.lugar?.nombre ? ` · ${c.lugar.nombre}` : ""}{c.lugar?.ciudad ? `, ${c.lugar.ciudad}` : ""}</p></div>
            </div>
          ))}
        </div>
      )}
      {entries.length === 0 && <p className="text-sm text-muted-foreground">{t("timeline.empty")}</p>}
      <ol className="relative border-l border-border ml-2 space-y-4">
        {entries.map((e, idx) => {
          if (e.kind === "corona") return (
            <li key="corona" data-testid="timeline-coronada" className="ml-4">
              <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-[#d4af37] gold-glow" />
              <p className="text-sm flex items-center gap-2"><Crown size={14} className="text-[#d4af37]" /> {t("timeline.crownedOn", { d: fmtFecha(e.fecha) })}</p>
            </li>);
          if (e.kind === "red_flag" || e.kind === "red_flag_off") return (
            <li key={e.kind} data-testid={`timeline-${e.kind}`} className="ml-4">
              <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full" style={{ background: e.kind === "red_flag" ? RED : "transparent", border: `1px solid ${RED}` }} />
              <p className="text-sm flex items-center gap-2"><RedFlagIcon size={14} testId={`timeline-${e.kind}-icon`} /> {t(e.kind === "red_flag" ? "timeline.redFlagOn" : "timeline.redFlagOffOn", { d: fmtFecha(e.fecha) })}</p>
            </li>);
          if (e.kind === "cita") return (
            <li key={e.data.id} className="ml-4 opacity-70" data-testid={`timeline-cita-${e.data.id}`}>
              <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full border border-muted-foreground bg-card" />
              <p className="text-sm">{t("timeline.appointment", { e: t(`appt.estado.${e.data.estado}`, { defaultValue: e.data.estado }).toLowerCase(), t: tipoLabel(e.data.tipo) })}</p>
              <p className="text-xs text-muted-foreground">{fmtFechaHora(e.data.inicio)}{e.data.lugar?.nombre ? ` · ${e.data.lugar.nombre}` : ""}</p>
            </li>);
          const i = e.data; const Icon = TIPO_ICON[i.tipo];
          const otros = (i.personas || []).filter((p) => p.id !== person.id).map((p) => p.nombre);
          return (
            <li key={i.id} className="ml-4 group" data-testid={`timeline-item-${i.id}`}>
              <span className="absolute -left-[9px] w-[18px] h-[18px] rounded-full bg-secondary border border-border flex items-center justify-center"><Icon size={10} /></span>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{i.tipo === "evento" && i.nombre_evento ? i.nombre_evento : tipoLabel(i.tipo)}{i.origen === "cita" && <span className="ml-2 text-[10px] uppercase text-muted-foreground">{t("timeline.fromAppointment")}</span>}</p>
                  <p className="text-xs text-muted-foreground">{fmtFecha(i.fecha)}{i.fecha_fin ? ` → ${fmtFecha(i.fecha_fin)}` : ""}
                    {(i.lugar?.nombre || i.lugar?.ciudad) && <> · <MapPin size={11} className="inline" /> {[i.lugar.nombre, i.lugar.ciudad].filter(Boolean).join(", ")}</>}
                    {fmtMonto(i.monto, i.moneda) && <> · {fmtMonto(i.monto, i.moneda)}</>}
                    {otros.length > 0 && <> {t("common.with", { n: otros.join(", ") })}</>}</p>
                  {i.notas && <p className="text-xs mt-1">{i.notas}</p>}
                </div>
                <div className="flex shrink-0">
                  <button data-testid={`timeline-edit-${i.id}`} onClick={() => openInteraction(i)} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                  <button data-testid={`timeline-delete-${i.id}`} onClick={() => remove(i.id)} className="p-1.5 text-muted-foreground hover:text-[#f43f5e]"><Trash2 size={14} /></button>
                </div>
              </div>
            </li>);
        })}
      </ol>
    </div>
  );
}
